import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, ShieldCheck, TrendingUp, Briefcase, Truck, Lock, CalendarClock, Building2, ClipboardCheck } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { FadeIn } from "@/components/Reveal";
import { SITE } from "@/config/site";

const INDICATORS = [
  { icon: Briefcase, label: "Professional Business Services" },
  { icon: Truck, label: "Mobile Service Available" },
  { icon: Lock, label: "Confidential Handling of Business Information" },
  { icon: CalendarClock, label: "Flexible Scheduling" },
  { icon: Building2, label: "Business-Focused Solutions" },
  { icon: ClipboardCheck, label: "Compliance & Operational Support" },
];

const PILLARS = [
  { icon: Lightbulb, word: "THINK", accent: "text-teal", copy: "Identify the risks, responsibilities, and operational gaps that may affect your business." },
  { icon: ShieldCheck, word: "SAFE", accent: "text-navy", copy: "Establish systems, safeguards, and documented procedures that support business readiness." },
  { icon: TrendingUp, word: "MITIGATION", accent: "text-amberdeep", copy: "Implement processes to reduce, monitor, and manage risk as the business grows." },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "About TSM Group CLE | Think Safe Mitigation";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "TSM Group CLE is a Northeast Ohio business risk management, compliance, and operational services company dedicated to helping entrepreneurs build stronger business foundations. Our philosophy: Think Safe Mitigation.");
  }, []);

  return (
    <div className="bg-paper" data-testid="about-page">
      <PageHero
        testid="about-hero"
        eyebrow="About TSM"
        title={<>Stronger foundations for <span className="text-gold">growing businesses.</span></>}
        copy="TSM Group CLE is a Northeast Ohio business risk management, compliance, and operational services company dedicated to helping entrepreneurs build stronger business foundations."
      />

      <section className="py-24 lg:py-32">
        <div className="container-tsm max-w-[1000px]">
          <FadeIn>
            <p className="text-lg leading-relaxed text-ink/80 lg:text-xl" data-testid="about-intro">
              Through education, technology, and practical service delivery, TSM helps businesses identify risks,
              organize critical processes, and strengthen operational readiness.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-6 leading-relaxed text-ink/70" data-testid="about-philosophy">
              Our guiding philosophy is simple: <span className="font-extrabold text-tealdeep">Think Safe Mitigation</span>.
              Risk cannot always be eliminated, but it can be identified, evaluated, and managed — and a business
              that manages its risks moves faster with confidence.
            </p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="mt-6 leading-relaxed text-ink/70" data-testid="about-model">
              The TSM4 product suite brings that philosophy to life: MITIVISE, our digital risk and compliance
              platform, alongside background screening and planned workforce services — backed by hands-on services
              like notary, document, and mobile business support.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="border-y border-navy/5 bg-white py-24 lg:py-32">
        <div className="container-tsm">
          <FadeIn className="max-w-3xl">
            <p className="eyebrow">The TSM Approach</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="about-pillars-headline">
              Three pillars behind everything we build.
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="about-pillars">
            {PILLARS.map((p, i) => (
              <FadeIn key={p.word} delay={i * 0.1} className="h-full">
                <article className="h-full rounded-xl border border-navy/10 bg-paper p-8">
                  <p.icon className={`h-8 w-8 ${p.accent}`} strokeWidth={1.8} />
                  <h3 className={`mt-4 text-3xl font-extrabold tracking-tight text-navy ${p.accent}`}>{p.word}</h3>
                  <p className="mt-4 leading-relaxed text-ink/70">{p.copy}</p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="container-tsm max-w-[1000px]">
          <FadeIn className="max-w-2xl">
            <p className="eyebrow">Why Businesses Choose TSM</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="about-trust-headline">
              Operational support you can count on.
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-trust-grid">
            {INDICATORS.map((item, i) => (
              <FadeIn key={item.label} delay={i * 0.06} className="h-full">
                <div className="flex h-full items-start gap-4 rounded-xl border border-navy/10 bg-white p-6">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-navy text-gold">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <p className="pt-1.5 font-semibold leading-snug text-navy">{item.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.2}>
            <div className="mt-14 flex flex-col items-center gap-6 rounded-xl bg-navy p-10 text-center text-white" data-testid="about-cta">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">{SITE.tagline}</p>
              <h3 className="max-w-2xl text-3xl font-extrabold tracking-tight">Know the risk. Make your next move.</h3>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link to="/tsm4" data-testid="about-cta-tsm4" className="btn-gold">Explore TSM4<ArrowRight className="h-4 w-4" /></Link>
                <Link to="/contact" data-testid="about-cta-contact" className="btn-outline-light">Talk With TSM</Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
